import {Modal} from "react-bootstrap-v5";
import React, {useEffect, useRef, useState} from "react";
import {editGroup, fetchGroupList} from "../../api";
import {ModalProps} from "../../models/Modal";
import {KTSVG} from "../../../../../_metronic/helpers";
import Alert from "../../../../components/alerts/Alert";
import {Action, ActionOrNull, Scope, ScopeOrNull, SimCard, SimCardGroup} from "../../models/Models";
import {AlertType} from "../../../../components/alerts";

type GroupId = number;

export interface SimCardGroupWithScopedItems extends SimCardGroup {
    scope?: ScopeOrNull
}

interface ISetGroupAssignmentsForm extends ModalProps {
    selectedRows: Partial<SimCard>[]
}

/**
 * Форма редактирования вхождения номера в группы
 */
export const SetGroupAssignmentsForm = ({selectedRows, show, handleClose, onChange}: ISetGroupAssignmentsForm) => {
    const [, refresh] = useState(0)
    const [message, setMessage] = useState('')
    const [disabled, setDisabled] = useState(true)
    const diffMap = useRef<Map<number, ActionOrNull>>(new Map())
    const [isSubmitted, setIsSubmitted] = useState(false)
    const initialGroupList = useRef<SimCardGroupWithScopedItems[]>([])
    const [updatedGroupList, setUpdatedGroupList] = useState<SimCardGroupWithScopedItems[] | undefined>()

    useEffect(() => {
            /**
             * Карта степени вхождения выделенных номеров в группы
             * 1. full - все выделенные номера входят в группу (кнопка выбора группы в виджете назначения групп нажата полностью)
             * 2. partial - только часть номеров входит в группу (кнопка выбора группы нажата частично, соответствует чекбоксу в промежуточном состоянии (с минусом))
             * 3. null - ни один номер не входит в группу (кнопка полностью отжата). Эта категория в карте отсутствует, но учитывается в селекторе назначения номеров группам.
             */
            const getMsisdnToGroupAssignmentsScopeMap = () => {
                // Подсчитываем кол-во номеров в каждой группе
                const groupIdCounterMap = new Map<GroupId, number>()
                selectedRows.forEach((item) => {
                    if (item.groups!!.length !== 0) {
                        for (const group of item.groups!!) {
                            if (groupIdCounterMap.get(group.id) === undefined) {
                                groupIdCounterMap.set(group.id, 1)
                            } else {
                                groupIdCounterMap.set(group.id, groupIdCounterMap.get(group.id)! + 1)
                            }
                        }
                    }
                })

                // Проставляем статусы в зависимости от подсчитанного кол-ва номеров
                const groupAssignmentsScopeMap = new Map<GroupId, ScopeOrNull>()
                for (const [groupId] of groupIdCounterMap) {
                    groupAssignmentsScopeMap.set(groupId, groupIdCounterMap.get(groupId) === selectedRows.length ? Scope.FULL : Scope.PARTIAL)
                }
                return groupAssignmentsScopeMap
            }

            /**
             * 1. Добавление в группы свойства scope согласно вхождению номеров в группы при первоначальной загрузке
             * 2. Заполнение референсного списка групп initialGroupList и создание нулевой карты различий diffMap для сравнения с обновляемым списком групп updatedGroupList
             */
            const getResponse = async () => {
                const response = (await fetchGroupList())
                const scoreMap = getMsisdnToGroupAssignmentsScopeMap()

                switch (response.status) {
                    case "ok":
                        const groupList: SimCardGroup[] = response.data.data.items
                        if (groupList) {
                            if (groupList.length !== 0) {
                                setUpdatedGroupList(
                                    groupList.map((item, index) => {
                                        diffMap.current.set(item.id, null)
                                        initialGroupList.current[index] = {
                                            ...item,
                                            scope: scoreMap.get(item.id) || null
                                        }
                                        let clone: SimCardGroupWithScopedItems = {id: 0, name: ''}
                                        Object.assign(clone, initialGroupList.current[index])
                                        return clone
                                    })
                                )
                            } else {
                                // Для вывода сообщения об отсутствии групп лишь после того, как отработает запрос
                                setUpdatedGroupList([])
                            }
                        }
                        return true
                    default:
                        console.log(response)
                        setMessage("Сервер сообщил об ошибке. Попробуйте выполнить операцию позднее.")
                        return false
                }
            }

            getResponse()
        },
        [selectedRows]
    )

    /**
     * Карта изменений [[groupId => Action]]
     */
    const buildDiffMap = () => {
        let action: ActionOrNull = null
        updatedGroupList?.forEach((item, index) => {
            switch (initialGroupList.current[index].scope) {
                case Scope.FULL:
                    action = item.scope === Scope.FULL ? null : Action.DELETE
                    break
                case Scope.PARTIAL:
                    if (item.scope === Scope.PARTIAL) {
                        action = null
                    } else {
                        action = item.scope === Scope.FULL ? Action.ADD : Action.DELETE
                    }
                    break
                default:
                    action = item.scope === Scope.FULL ? Action.ADD : null
            }
            diffMap.current.set(item.id, action)
        })
    }

    useEffect(() => {
        const getResponse = () => {
            // Список ID групп, по которым имеются изменения
            const diff = Array.from(diffMap.current)
                .filter((item) => item[1] !== null)

            if (diff.length !== 0) {
                const msisdnList = selectedRows.map((simCard) => simCard.msisdn!.toString())
                let countDown = diff.length
                updatedGroupList?.forEach(async (item) => {
                    if (diffMap.current.get(item.id) !== null) {
                        const response = await editGroup(item.id, diffMap.current.get(item.id)!, msisdnList)
                        switch (response.status) {
                            case "ok":
                                if (--countDown === 0) {
                                    onChange && onChange()
                                    handleClose()
                                }
                                return
                            case "error":
                                console.log(response)
                                setIsSubmitted(false)
                                setMessage("Сервер не смог обработать запрос. Попробуйте выполнить сохранение в группу позднее.")
                                return false
                        }
                    }
                })
            }
        }

        isSubmitted && getResponse()
    }, [isSubmitted])   // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Сброс формы при открытии модального окна
     */
    const reset = () => {
        setMessage('')
        setIsSubmitted(false)
    }

    /**
     * Вносит изменения в набор состояний групп согласно нажатию по кнопке группы и строит карту изменений по сравнению с первоначальным состоянием.
     * При наличии наполовину активных кнопок ('partial') и нажатию на одной из них возврат в состояние 'partial' будет невозможен.
     */
    const onGroupSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const targetGroup = updatedGroupList?.find((item) => item.id.toString() === event.currentTarget.value) as SimCardGroupWithScopedItems
        // При выборе scope возможны лишь 2 варианта - "все номера входят в группу" или "ни один не входит".
        if (event.currentTarget.checked) {
            targetGroup.scope = Scope.FULL
        } else {
            targetGroup.scope = null
        }
        buildDiffMap()
        setButtonStatus()
        refresh(Math.random())
    }

    /**
     * Дизейблим кнопку сохранения при отсутствии изменений
     */
    const setButtonStatus = () => {
        const diff = Array.from(diffMap.current.values()).filter((item) => item !== null).length
        setDisabled(diff === 0)
    }

    /**
     * Стилизация кнопок групп в зависимости от состояния параметра scope
     */
    const getButtonStyle = (index: number) => {
        switch (updatedGroupList!![index].scope) {
            case 'full':
                return ' border border-active active btn-active-light-primary'
            case 'partial':
                return ' border-dashed border-dashed-active active btn-active-light-info'
            default:
                return ''
        }
    }

    return (
        <Modal
            className='modal'
            role='dialog'
            aria-hidden='true'
            tabIndex='-1'
            show={show}
            dialogClassName="modal-dialog modal-dialog-scrollable w-350px"
            onShow={reset}
            onHide={handleClose}
        >
            <div className='modal-header py-4'>
                <h3>Вхождение номера в группы</h3>

                <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={() => handleClose()}>
                    <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1'/>
                </div>
            </div>

            <div className='modal-body  pt-5 px-10 center'>
                {updatedGroupList && updatedGroupList.length !== 0 &&
                <>
                    <div className="d-flex flex-column mb-5">
                        {updatedGroupList?.map((item, index) => {
                            return (
                                <React.Fragment key={index}>
                                    <input
                                        type='checkbox'
                                        className='btn-check border-'
                                        name='activityStatus'
                                        id={'_' + index}
                                        value={item.id}
                                        onFocus={(event) => event.stopPropagation()}
                                        onChange={onGroupSelect}
                                        checked={updatedGroupList[index].scope === 'full'}
                                    />
                                    <label
                                        className={`btn btn-lg btn-outline btn-bg-light btn-color-gray-600 px-4 m-2 ${getButtonStyle(index)}`}
                                        htmlFor={'_' + index}
                                    >
                                        <span className='text-gray-800 fw-bold'>{item.name}</span>
                                    </label>
                                </React.Fragment>
                            )
                        })}
                    </div>
                    <div className="d-flex justify-content-end">
                        <button className="btn btn-primary" onClick={() => setIsSubmitted(true)} disabled={disabled}>Сохранить</button>
                    </div>
                </>
                }

                {updatedGroupList && updatedGroupList.length === 0 &&
                    <Alert
                        type={AlertType.INFO}
                        additionalClassNames={"p-5 mb-0"}
                        message={"Вы пока не создали ни одной группы. Это можно сделать в разделе <a href='/simcard/groups'>Группы SIM-карт</a>"}
                    />
                }

                <div>
                    {message && <Alert message={message}/>}
                </div>
            </div>
        </Modal>
    )
}
